import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car, CheckCircle2, KeyRound, Wrench, FileClock, ClipboardCheck, ShieldAlert,
  AlertTriangle, Wallet, Gauge, TrendingUp, ArrowUpRight, Gavel, Undo2, IdCard,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area,
} from "recharts";
import api from "@/lib/api";
import { StatCard } from "@/components/common/StatCard";
import { PageHeader } from "@/components/common/PageHeader";
import { DeadlineBadge } from "@/components/common/Badges";
import { Skeleton } from "@/components/ui/skeleton";
import { eur, km, fdate } from "@/lib/format";

const CHART_COLORS = ["#001A3B", "#12386B", "#A5894E", "#4A5568", "#B4552C", "#7A9B8E"];

const ACTIVITY_ICON = {
  vehicle_added: Car, assignment_created: KeyRound, maintenance: Wrench,
  accident: ShieldAlert, document_added: ClipboardCheck, mileage_updated: Gauge,
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div>
        <PageHeader title="Gestion de flotte" subtitle="Pilotez votre flotte de véhicules en temps réel." />
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>
      </div>
    );
  }

  const k = data.kpis;
  const kpis = [
    { icon: Car, label: "Véhicules", value: k.total_vehicles, tone: "green", sub: k.returned ? `Flotte active · ${k.returned} restitué(s) exclu(s)` : "Flotte active", testid: "kpi-total-vehicles", to: "/vehicles?statut=actifs" },
    { icon: CheckCircle2, label: "Disponibles", value: k.available, tone: "sage", testid: "kpi-available", to: "/vehicles?statut=disponible" },
    { icon: KeyRound, label: "Affectés", value: k.assigned, tone: "slate", testid: "kpi-assigned", to: "/vehicles?statut=affecte" },
    { icon: Wrench, label: "En maintenance", value: k.maintenance, tone: "amber", testid: "kpi-maintenance", to: "/vehicles?statut=maintenance" },
    { icon: FileClock, label: "Contrats à échéance", value: k.contracts_soon, tone: "gold", sub: "< 30 jours", testid: "kpi-contracts", to: "/contracts?echeance=30" },
    { icon: ClipboardCheck, label: "CT à venir", value: k.ct_soon, tone: "green", sub: "< 30 jours", testid: "kpi-ct", to: "/deadlines?type=Contrôle technique&jours=30" },
    { icon: ShieldAlert, label: "Assurances", value: k.insurances_soon, tone: "gold", sub: "à renouveler", testid: "kpi-insurances", to: "/insurances?echeance=30" },
    { icon: AlertTriangle, label: "Sinistres ouverts", value: k.accidents_open, tone: "red", testid: "kpi-accidents", to: "/accidents?statut=ouverts" },
  ];
  const kpis2 = [
    { icon: Undo2, label: "Restitutions à venir", value: k.restitutions_soon ?? 0, tone: "gold", sub: "< 30 jours", testid: "kpi-restitutions", to: "/deadlines?type=Restitution&jours=30" },
    { icon: Gavel, label: "Contraventions ouvertes", value: k.contraventions_open ?? 0, tone: "gold", sub: "à traiter / en attente", testid: "kpi-contraventions", to: "/contraventions?statut=open" },
    { icon: IdCard, label: "Permis à échéance", value: k.licenses_soon ?? 0, tone: "red", sub: "< 30 jours", testid: "kpi-licenses", to: "/drivers?permis=30" },
  ];

  return (
    <div>
      <PageHeader title="Gestion de flotte" subtitle="Pilotez votre flotte de véhicules en temps réel." />

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {kpis.map((kp) => <StatCard key={kp.testid} {...kp} />)}
      </div>

      {/* Financial row */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard icon={Wallet} label="Dépenses du mois" value={eur(k.month_expenses)} tone="green" sub="Toutes catégories" testid="kpi-month-expenses" to={`/expenses?mois=${k.month || ""}`} />
        <StatCard icon={TrendingUp} label="Dépenses totales" value={eur(k.total_expenses)} tone="sage" sub="Y compris véhicules restitués" testid="kpi-total-expenses" to="/expenses" />
        <StatCard icon={Gauge} label="Kilométrage total" value={km(k.total_mileage)} tone="slate" testid="kpi-total-mileage" to="/vehicles?statut=actifs&tri=km_desc" />
        {kpis2.map((kp) => <StatCard key={kp.testid} {...kp} />)}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Fleet state */}
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h2 className="font-heading text-lg font-semibold text-[#18221F]">État de la flotte</h2>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.fleet_state} dataKey="count" nameKey="status" innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {data.fleet_state.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {data.fleet_state.map((s, i) => (
              <div key={s.key} className="flex items-center gap-2 text-sm">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                <span className="text-[#4A5568]">{s.status}</span>
                <span className="ml-auto font-semibold text-[#18221F]">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly expenses */}
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold text-[#18221F]">Dépenses mensuelles</h2>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthly_expenses}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#001A3B" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#001A3B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDF1F6" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#8695AB" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#8695AB" }} tickLine={false} axisLine={false} width={50} />
                <Tooltip formatter={(v) => eur(v)} />
                <Area type="monotone" dataKey="amount" stroke="#001A3B" strokeWidth={2.5} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Alerts */}
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold text-[#18221F]">Alertes &amp; échéances</h2>
            <button onClick={() => navigate("/deadlines")} className="text-sm font-medium text-[#001A3B] hover:underline" data-testid="dashboard-see-all-deadlines">Tout voir</button>
          </div>
          <div className="mt-4 space-y-2" data-testid="dashboard-alerts">
            {data.alerts.length === 0 && <p className="py-8 text-center text-sm text-[#8695AB]">Aucune alerte — tout est à jour ✓</p>}
            {data.alerts.slice(0, 8).map((a, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border border-[#EDF0F5] px-3 py-2.5">
                <DeadlineBadge level={a.level}>{a.type}</DeadlineBadge>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[#18221F]">{a.label}</p>
                  <p className="truncate text-xs text-[#8695AB]">{a.vehicle || "—"}</p>
                </div>
                <div className="text-right text-xs">
                  <div className="font-medium text-[#18221F]">{fdate(a.date)}</div>
                  <div className="text-[#8695AB]">{a.days != null ? (a.days < 0 ? `${Math.abs(a.days)} j dépassé` : `dans ${a.days} j`) : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Activities */}
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h2 className="font-heading text-lg font-semibold text-[#18221F]">Dernières activités</h2>
          <div className="mt-4 space-y-4">
            {data.activities.length === 0 && <p className="py-8 text-center text-sm text-[#8695AB]">Aucune activité récente</p>}
            {data.activities.map((act) => {
              const Icon = ACTIVITY_ICON[act.kind] || Car;
              return (
                <div key={act.id} className="flex gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EEF2F8] text-[#001A3B]"><Icon className="h-4 w-4" /></div>
                  <div className="min-w-0">
                    <p className="text-sm text-[#18221F]">{act.title}</p>
                    <p className="text-xs text-[#8695AB]">{fdate(act.created_at)} · {act.user_name || "Système"}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top costly */}
      <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6">
        <h2 className="font-heading text-lg font-semibold text-[#18221F]">Top véhicules les plus coûteux</h2>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {data.top_vehicles.map((v) => (
            <button key={v.vehicle_id} onClick={() => navigate(`/vehicles/${v.vehicle_id}`)} className="group overflow-hidden rounded-xl border border-[#E2E8F0] text-left transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid="top-vehicle-card">
              <div className="h-24 w-full overflow-hidden bg-[#EEF2F8]">
                {v.photo && <img src={v.photo} alt={v.label} className="h-full w-full object-cover transition-transform group-hover:scale-105" />}
              </div>
              <div className="p-3">
                <p className="truncate text-sm font-semibold text-[#18221F]">{v.label}</p>
                <p className="font-mono text-xs text-[#8695AB]">{v.registration}</p>
                <p className="mt-1 flex items-center gap-1 text-sm font-bold text-[#001A3B]">{eur(v.cost)} <ArrowUpRight className="h-3 w-3" /></p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
